<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- ============================================================
                 HERO SECTION (Light)
                 ============================================================ -->
            <section class="mkt-hero-light" style="padding: 80px 0 0;">
                <div class="mkt-container">
                    <div class="row items-center q-col-gutter-xl">
                        <div class="col-12 col-md-6">
                            <!-- Above-the-fold AI tie-in pill. Sparkle icon + gradient
                                 fill. Surfaces the "Driveable by AI" capability as the
                                 first thing visitors read. The "self-serve" line moved
                                 to the trust-ticks. -->
                            <div class="q-mb-lg">
                                <span class="mkt-ai-pill">
                                    <q-icon name="auto_awesome" class="mkt-ai-pill-icon" />
                                    <span>Built for humans. <strong>Driveable by AI.</strong></span>
                                </span>
                            </div>
                            <!-- Hero pitch - short and direct. The right column does
                                 the heavy lifting by showing what's behind the legacy
                                 velvet rope (REST API, SSO, audit, MCP) at our prices. -->
                            <h1 class="q-mb-md" style="font-size:clamp(2.2rem,5vw,3.5rem);font-weight:800;line-height:1.05;color:#0F172A;letter-spacing:-0.025em;">
                                Electronic signatures without<br>
                                <span class="text-gradient-indigo">the headaches.</span>
                                <span class="visually-hidden"> - eSign Launchpad</span>
                            </h1>
                            <p class="q-mb-lg" style="font-size:1.25rem;line-height:1.5;max-width:520px;color:#475569;font-weight:500;">
                                Everything you want. Nothing you don't.
                            </p>
                            <div class="row q-gutter-md q-mb-lg mkt-hero-cta-row">
                                <q-btn color="primary" size="lg" label="Start free" href="/Account/Register"
                                    icon-right="arrow_forward"
                                    no-caps unelevated style="border-radius:10px;padding:10px 32px;font-weight:600;" />
                                <q-btn outline size="lg" no-caps label="See pricing" href="/Home/Pricing"
                                    style="border-radius:10px;padding:10px 32px;color:#334155;border-color:#CBD5E1;" />
                            </div>
                            <div class="row q-gutter-lg" style="flex-wrap:wrap;">
                                <div class="row items-center">
                                    <q-icon name="check_circle" color="positive" size="18px" class="q-mr-xs" />
                                    <span style="font-size:13px;color:#475569;">Self-serve · no sales call</span>
                                </div>
                                <div class="row items-center">
                                    <q-icon name="check_circle" color="positive" size="18px" class="q-mr-xs" />
                                    <span style="font-size:13px;color:#475569;">No credit card required</span>
                                </div>
                                <div class="row items-center">
                                    <q-icon name="check_circle" color="positive" size="18px" class="q-mr-xs" />
                                    <span style="font-size:13px;color:#475569;">Cancel anytime</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-md-6">
                            <!-- Hero feature highlights: 4 cards rotating every 4s,
                                 each highlighting a "big-bang" capability that legacy
                                 e-sign vendors gate behind enterprise contracts but we
                                 deliver self-serve. Eyebrow chip = the velvet-rope
                                 bypass. Stack uses an absolute-positioned card stage so
                                 swapping the card never changes container height (no
                                 jitter even when one card has more body lines). -->
                            <div class="mkt-hero-carousel"
                                 @mouseenter="pauseHeroCarousel"
                                 @mouseleave="resumeHeroCarousel">
                                <div class="mkt-hero-stage">
                                    <div v-for="(f, fi) in heroFeatures" :key="f.key"
                                         class="mkt-hero-card"
                                         :class="{ 'mkt-hero-card--active': fi === heroIdx }"
                                         :style="`--accent:${f.color};`">
                                        <!-- Velvet-rope bypass eyebrow -->
                                        <div class="mkt-hero-card-eyebrow">
                                            <q-icon name="bolt" size="14px" />
                                            <span>{{ f.eyebrow }}</span>
                                        </div>

                                        <!-- Big stat + label -->
                                        <div class="row items-end q-gutter-sm q-mt-md q-mb-md">
                                            <div class="mkt-hero-card-stat" :style="`background:linear-gradient(135deg,${f.color},${f.colorTo});-webkit-background-clip:text;background-clip:text;color:transparent;`">{{ f.stat }}</div>
                                            <div class="mkt-hero-card-statlabel">{{ f.statLabel }}</div>
                                        </div>

                                        <!-- Title + body -->
                                        <div class="mkt-hero-card-title">{{ f.title }}</div>
                                        <p class="mkt-hero-card-body">{{ f.body }}</p>

                                        <!-- Bottom anchor: contrast against legacy -->
                                        <div class="mkt-hero-card-anchor">
                                            <q-icon name="check_circle" size="14px" />
                                            <span>{{ f.anchor }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Caption + dot indicators -->
                                <div class="mkt-hero-mockcaption">
                                    <div class="text-weight-medium" style="font-size:0.78rem;color:#475569;">{{ heroFeatures[heroIdx].caption }}</div>
                                    <div class="mkt-hero-dots">
                                        <button v-for="(f, fi) in heroFeatures" :key="f.key"
                                                class="mkt-hero-dot"
                                                :class="{ 'mkt-hero-dot--active': fi === heroIdx }"
                                                @click="setHeroIdx(fi)"
                                                :aria-label="`Show ${f.title}`"></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Built On / Powered By logo strip - grayscale partner row.
                     These are the four real infrastructure partners actually in
                     use (Azure for cloud, Stripe for payments, Persona for IDV,
                     Authenticate.com for KBA). Single horizontal row, neutral
                     tones; pops to color on hover. -->
                <div class="mkt-trust-strip" style="margin-top:60px;padding:32px 0;background:#F8FAFC;border-top:1px solid #E2E8F0;border-bottom:1px solid #E2E8F0;">
                    <div class="mkt-container">
                        <div class="text-center q-mb-md">
                            <span style="font-size:0.78rem;font-weight:700;letter-spacing:1.5px;color:#64748B;text-transform:uppercase;">
                                Built on infrastructure you already trust
                            </span>
                        </div>
                        <div class="row justify-center items-center" style="flex-wrap:wrap;gap:48px;">
                            <div v-for="p in partners" :key="p.name" class="mkt-trust-logo row items-center q-gutter-sm">
                                <q-icon :name="p.icon" size="28px" />
                                <div>
                                    <div class="text-weight-bold" style="font-size:0.95rem;line-height:1.1;">{{ p.name }}</div>
                                    <div style="font-size:0.7rem;line-height:1.1;letter-spacing:0.3px;text-transform:uppercase;">{{ p.role }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 STATS STRIP - big-number callouts. Replaces wall-of-text
                 capability lists with at-a-glance scale signals.
                 ============================================================ -->
            <section class="mkt-stats-strip reveal-scale" v-intersection="statsRevealCfg" style="padding:48px 0;background:#fff;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-md justify-center text-center">
                        <div v-for="(s, idx) in stats" :key="s.label" class="col-6 col-sm-4 col-md-2">
                            <div class="text-weight-bold" style="font-size:clamp(1.8rem,3vw,2.4rem);line-height:1;color:#0F172A;letter-spacing:-0.02em;">
                                <span :style="`background:linear-gradient(135deg,${s.from},${s.to});-webkit-background-clip:text;background-clip:text;color:transparent;`">{{ s.prefix || '' }}{{ statCounters[idx] }}{{ s.suffix || '' }}</span>
                            </div>
                            <div class="q-mt-xs" style="font-size:0.78rem;letter-spacing:0.6px;text-transform:uppercase;color:#64748B;font-weight:600;">{{ s.label }}</div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 SPEED BAND. Single-line value prop punctuating the
                 stats strip (numbers) and the persona selector ("which
                 one are you"). Reads like a confident summary: this
                 is what all those stats and capabilities add up to.

                 Headline is the literal time-to-first-signature; the
                 sub-line carries brand voice (confident, plain-spoken,
                 the opposite of legacy e-sign vendor positioning).
                 ============================================================ -->
            <section class="mkt-speed-band reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <div class="mkt-speed-eyebrow">
                        <q-icon name="bolt" size="14px" class="q-mr-xs" style="vertical-align:-2px;" />
                        Self-serve onboarding
                    </div>
                    <h2 class="mkt-speed-headline">
                        Sign-up to signed in
                        <span class="mkt-speed-accent">5 minutes.</span>
                    </h2>
                    <p class="mkt-speed-subhead">
                        eSign isn't rocket science. We stopped pretending it was.
                    </p>
                </div>
            </section>

            <!-- ============================================================
                 PERSONA SELECTOR - self-funneling above the fold
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:50px 0 40px;">
                <div class="mkt-container">
                    <div class="text-center q-mb-lg">
                        <h2 class="text-weight-bold q-mb-xs" style="font-size:clamp(1.4rem,2.6vw,1.9rem);color:#0a0f1a;">
                            What brings you here?
                        </h2>
                        <p class="text-grey-7" style="font-size:1rem;">Pick the card that sounds most like you. We'll show you the right plan.</p>
                    </div>
                    <div class="row q-col-gutter-lg justify-center">
                        <div class="col-12 col-sm-6 col-md-4" v-for="p in personaCards" :key="p.key">
                            <div class="mkt-persona-card column items-center text-center"
                                 style="height:100%;padding:28px 22px;border:1px solid #E2E8F0;border-radius:14px;background:#fff;transition:all 0.15s ease;">
                                <div style="font-size:40px;margin-bottom:10px;">{{ p.emoji }}</div>
                                <div class="text-weight-bold" style="font-size:1.15rem;color:#0F172A;line-height:1.2;">{{ p.label }}</div>
                                <div class="q-mt-sm text-grey-7" style="font-size:0.92rem;line-height:1.5;min-height:3em;">{{ p.subtitle }}</div>
                                <div class="q-mt-md full-width" style="border-top:1px solid #EEF2F7;padding-top:12px;">
                                    <div class="text-weight-bold" :style="`font-size:0.9rem;color:${p.tierColor};`">{{ p.tierName }}</div>
                                    <div class="text-grey-6 q-mb-md" style="font-size:0.82rem;">{{ p.tierPrice }}</div>
                                    <q-btn unelevated no-caps
                                           color="primary"
                                           :label="`Explore ${p.label.replace(/^For\s+/,'')}`"
                                           icon-right="arrow_forward"
                                           :href="p.href"
                                           style="border-radius:8px;padding:6px 18px;font-weight:500;" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="text-center q-mt-lg">
                        <q-btn flat no-caps color="primary" label="Not sure? Compare all plans"
                               icon-right="arrow_forward" href="/Home/Pricing"
                               style="font-size:0.9rem;font-weight:500;border-radius:8px;" />
                    </div>
                </div>
            </section>

            <!-- Soft curve divider: persona selector → pain points -->
            <svg class="mkt-divider-soft" viewBox="0 0 1440 56" preserveAspectRatio="none">
                <path d="M0,0 C320,48 720,56 1080,32 C1260,20 1360,24 1440,16 L1440,56 L0,56 Z" fill="#ffffff" />
            </svg>

            <!-- ============================================================
                 PRINCIPLES - three declarative commitments that shape the
                 product. The headline IS the section: three single words,
                 each with a separator period for a hard-stop cadence. The
                 short subtitle frames them as commitments, not aspirations.
                 Each q-card uses column flex so headers/descriptions/anchors
                 align across the row regardless of copy length.
                 Tone is mkt-light (not white) so it separates cleanly from the
                 white persona-selector above - the soft curve divider between
                 them now reads as a visible white-to-gray transition, and the
                 white bordered cards gain definition against the gray.
                 ============================================================ -->
            <section class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.8rem,3.2vw,2.4rem);color:#0a0f1a;letter-spacing:-0.02em;">
                            <span class="text-gradient-indigo">Predictable.</span> Focused. Fast.
                        </h2>
                        <p class="text-grey-7" style="font-size:1.05rem;max-width:560px;margin:0 auto;">
                            Three commitments that shape every product decision.
                        </p>
                    </div>
                    <div class="row q-col-gutter-lg items-stretch">
                        <div class="col-12 col-md-4" v-for="pain in painPoints" :key="pain.title">
                            <q-card flat bordered class="full-height column" style="border-radius:14px;">
                                <q-card-section>
                                    <q-icon :name="pain.icon" size="32px" :style="`color:${pain.color};`" class="q-mb-sm" />
                                    <div class="text-overline" :style="`color:${pain.color};letter-spacing:1.5px;font-weight:600;line-height:1.2;`">{{ pain.eyebrow }}</div>
                                    <div class="text-h6 text-weight-bold q-mt-xs" style="color:#0a0f1a;line-height:1.25;min-height:2.5em;">{{ pain.title }}</div>
                                </q-card-section>
                                <q-card-section class="col q-pt-none">
                                    <p class="text-grey-7 q-mb-none" style="font-size:0.9rem;line-height:1.6;">{{ pain.description }}</p>
                                </q-card-section>
                                <q-separator />
                                <q-card-section :style="`background:${pain.color}10;`">
                                    <div class="row no-wrap items-start">
                                        <q-icon name="check_circle" color="positive" size="20px" class="q-mr-sm q-mt-xs" style="flex-shrink:0;" />
                                        <span class="col text-weight-medium" :style="`color:${pain.color};font-size:0.9rem;line-height:1.5;`">{{ pain.solution }}</span>
                                    </div>
                                </q-card-section>
                            </q-card>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FEATURE SHOWCASE (Light gray with subtle wash)
                 ============================================================ -->
            <section class="mkt-light-wash reveal-scale" v-intersection="revealCfg" style="padding:80px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">
                            Everything you need to get documents signed
                        </h2>
                        <p class="text-grey-7" style="font-size:1.05rem;">Powerful features that make e-signatures effortless</p>
                    </div>

                    <!-- Feature tabs -->
                    <div class="row justify-center q-mb-lg" style="gap:8px;flex-wrap:wrap;">
                        <q-btn v-for="tab in featureTabs" :key="tab.key"
                            :color="activeTab === tab.key ? 'primary' : undefined"
                            :flat="activeTab !== tab.key"
                            :unelevated="activeTab === tab.key"
                            :text-color="activeTab !== tab.key ? 'grey-8' : undefined"
                            :label="tab.label"
                            :icon="tab.icon"
                            no-caps size="md"
                            style="border-radius:10px;"
                            @click="activeTab = tab.key" />
                    </div>

                    <!-- Tab content - single render of the active tab so the
                         <transition> can animate the swap in/out cleanly.
                         `mode="out-in"` waits for the old panel to leave before
                         the new one enters, so they don't visually stack. -->
                    <transition name="mkt-tab-fade" mode="out-in">
                        <div :key="activeTab" class="mkt-tab-panel">
                            <div class="row q-col-gutter-lg items-center">
                                <div class="col-12 col-md-6">
                                    <h3 class="text-weight-bold q-mb-sm" style="font-size:1.5rem;color:#0a0f1a;">{{ currentTab.heading }}</h3>
                                    <p class="text-grey-7 q-mb-lg" style="font-size:1rem;line-height:1.7;">{{ currentTab.description }}</p>
                                    <q-list dense class="mkt-feature-list">
                                        <q-item v-for="feature in currentTab.features" :key="feature" class="q-px-none">
                                            <q-item-section avatar top style="min-width:28px;padding-right:0;">
                                                <q-icon name="check_circle" color="positive" size="20px" />
                                            </q-item-section>
                                            <q-item-section>
                                                <q-item-label style="font-size:0.95rem;color:#333;line-height:1.55;">{{ feature }}</q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                </div>
                                <div class="col-12 col-md-6">
                                    <div class="mkt-tab-image-frame">
                                        <picture>
                                            <source type="image/webp"
                                                    :srcset="srcsetFor(currentTab, 'webp')"
                                                    sizes="(max-width: 767px) 100vw, 50vw" />
                                            <img :src="`/img/features/${currentTab.key}-${currentTab.image.widths[currentTab.image.widths.length - 1]}.jpg`"
                                                 :srcset="srcsetFor(currentTab, 'jpg')"
                                                 sizes="(max-width: 767px) 100vw, 50vw"
                                                 :alt="currentTab.image.alt"
                                                 :width="currentTab.image.intrinsicW"
                                                 :height="currentTab.image.intrinsicH"
                                                 loading="lazy"
                                                 decoding="async" />
                                        </picture>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </section>

            <!-- ============================================================
                 OVERVIEW SPOTLIGHTS - four zig-zagging callouts that sample
                 the four highest-leverage stories from the rest of the site
                 so a visitor landing here gets a complete picture without
                 having to click through. Reuses by design: each slide also
                 lives on its primary deep-dive page. Narrative order is
                 Free -> Pay -> AI -> Embed, mirroring the natural adoption
                 progression. Component default `wash` tone keeps each band
                 visually distinct from the surrounding mkt-light-wash and
                 mkt-white sections.
                 ============================================================ -->
            <marketing-spotlight
                slug="spot-free"
                alt="eSign Launchpad Free plan card - three signings per month forever with no credit card required"
                eyebrow-icon="celebration"
                icon-circle-class="mkt-icon-circle--green"
                title="Free, forever. No credit card."
                blurb="Three signings every month on the Free plan, every month. Pay $4.99 per signing only if you go over. No upsells, no card on file, no trial-then-billed surprise."
                :bullets="['3 free signings every month, forever', 'No credit card to start, ever', 'Pay-as-you-go overage only when you need it', 'Same audit trail and security as paid plans']"
                cta-label="Start free"
                cta-href="/Account/Register" />

            <marketing-spotlight
                slug="spot-sign-pay"
                alt="Collect deposits, retainers, and invoices during the signing ceremony - funds settle straight to your bank"
                eyebrow-icon="payments"
                icon-circle-class="mkt-icon-circle--gold"
                title="Sign and pay. In one flow."
                blurb="Collect retainers, deposits, and invoices during the signing ceremony itself. Funds settle straight to your Stripe-connected bank account - no separate payment links to chase."
                :bullets="['Auth-and-capture or charge-on-submission per package', 'Multi-payer batching with automatic refund on failure', 'Free-tier signers prepay per-package via Stripe Elements', 'Audit trail records the payment alongside the signature']"
                cta-label="See plans with payments"
                cta-href="/Home/Pricing"
                flip />

            <marketing-spotlight
                slug="spot-ai-agents"
                alt="eSign Launchpad MCP server lets Claude and other AI agents drive signing flows with 90+ tools"
                eyebrow-icon="auto_awesome"
                icon-circle-class="mkt-icon-circle--gold"
                title="Your AI agent can run your signing flows"
                blurb="Drop our Model Context Protocol server into Claude Desktop, Cline, Continue, or any MCP client. 90+ tools - the same surface as our REST API - drivable from natural language."
                :bullets="['90+ MCP tools, scoped per credential', 'Same pricing as REST - agent calls billed identically', 'Works with Claude Desktop and any MCP host', 'Send packages, add signers, void docs - all from a prompt']"
                cta-label="See the MCP server"
                cta-href="/Home/Developers#mcp" />

            <!-- ============================================================
                 HOW IT WORKS (White)
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:80px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">
                            Get documents signed in three steps
                        </h2>
                        <p class="text-grey-7" style="font-size:1.05rem;">No training required. No software to install.</p>
                    </div>
                    <div class="row q-col-gutter-xl items-stretch">
                        <div class="col-12 col-md-4" v-for="(step, i) in steps" :key="step.number">
                            <div class="mkt-step-tile">
                                <!-- Oversized 01 / 02 / 03 ribbon - the visual lead-in. -->
                                <div class="mkt-step-ribbon" style="font-family:'Inter',ui-sans-serif,system-ui,sans-serif;">{{ String(i + 1).padStart(2, '0') }}</div>
                                <div class="row items-center q-gutter-sm q-mb-sm">
                                    <q-icon :name="step.icon" size="28px" color="primary" />
                                    <h3 class="text-weight-bold q-ma-none" style="font-size:1.2rem;color:#0a0f1a;line-height:1.2;">{{ step.title }}</h3>
                                </div>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ step.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Wave: light to dark -->
            <svg class="mkt-wave-light-to-dark" viewBox="0 0 1440 60" preserveAspectRatio="none" style="display:block;width:100%;background:#ffffff;">
                <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="var(--mkt-navy)" />
            </svg>

            <!-- ============================================================
                 PRICING TEASER (Dark light)
                 ============================================================ -->
            <section class="mkt-dark-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);">
                        Plans starting at <span class="text-mkt-green">$0/month</span>
                    </h2>
                    <p class="text-grey-5 q-mb-xl" style="font-size:1.05rem;">Unlimited documents on every paid plan. No per-envelope fees. Ever.</p>

                    <!-- Live tier data only. If the server couldn't provide plans,
                         show a visible error rather than an empty row. -->
                    <div v-if="tierTeaser.length > 0" class="row q-col-gutter-md justify-center">
                        <div class="col-6 col-md-3" v-for="tier in tierTeaser" :key="tier.tierCode">
                            <div class="mkt-card--dark mkt-teaser-tile q-pa-md column items-center text-center"
                                 style="border-radius:12px;height:100%;"
                                 :style="tier.featured ? 'border-color: var(--mkt-indigo);' : ''">
                                <div class="text-grey-5 text-weight-medium q-mb-xs" style="font-size:0.85rem;">{{ tier.name }}</div>
                                <div class="text-white text-weight-bold" style="font-size:1.6rem;line-height:1.1;">{{ tier.price }}</div>
                                <div class="text-grey-6 q-mb-sm" style="font-size:0.75rem;">{{ tier.sub }}</div>
                                <q-btn flat no-caps dense size="sm"
                                       :color="tier.featured ? 'white' : 'grey-4'"
                                       label="Start"
                                       icon-right="arrow_forward"
                                       :href="`/Account/Register?plan=${tier.tierCode}`"
                                       class="q-mt-auto"
                                       style="border-radius:6px;font-size:0.8rem;" />
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center q-py-lg">
                        <q-icon name="cloud_off" size="32px" color="negative" class="q-mb-sm" />
                        <div class="text-grey-5" style="font-size:0.9rem;">
                            Pricing temporarily unavailable.
                            <a href="/" @click.prevent="reloadPage" class="text-white" style="font-weight:500;">Refresh</a>
                            or <a href="/Home/Support" class="text-white" style="font-weight:500;">contact support</a>.
                        </div>
                    </div>

                    <div class="q-mt-xl row justify-center q-gutter-md">
                        <q-btn color="primary" label="See All Plans" href="/Home/Pricing" no-caps unelevated size="lg"
                            style="border-radius:10px;padding:10px 40px;" />
                        <q-btn color="white" text-color="primary" outline label="Help Me Pick" href="/Home/PlanPicker" no-caps size="lg"
                            style="border-radius:10px;padding:10px 40px;border-color:rgba(255,255,255,0.3);" />
                    </div>
                </div>
            </section>

            <!-- Wave: dark to light -->
            <svg class="mkt-wave-dark-to-light" viewBox="0 0 1440 60" preserveAspectRatio="none" style="display:block;width:100%;background:var(--mkt-navy-light);">
                <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#ffffff" />
            </svg>

            <!-- ============================================================
                 TRUST & COMPLIANCE (White)
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">
                            Production-grade security built in
                        </h2>
                        <p class="text-grey-7" style="font-size:1.05rem;">Your documents are protected by Microsoft Azure, AES-256 encryption, and a hash-chained tamper-evident audit trail.</p>
                    </div>
                    <div class="row q-col-gutter-lg justify-center">
                        <div class="col-6 col-md-2 text-center" v-for="badge in trustBadges" :key="badge.label">
                            <div class="mkt-icon-circle mkt-icon-circle--green q-mb-sm" style="margin:0 auto;">
                                <q-icon :name="badge.icon" />
                            </div>
                            <div class="text-weight-bold" style="font-size:0.85rem;color:#0a0f1a;">{{ badge.label }}</div>
                            <div class="text-grey-6" style="font-size:0.75rem;">{{ badge.sub }}</div>
                        </div>
                    </div>
                    <div class="text-center q-mt-xl">
                        <a href="/Home/Security" class="text-primary text-weight-medium" style="font-size:0.95rem;">
                            Learn more about our security <q-icon name="arrow_forward" size="16px" />
                        </a>
                    </div>
                </div>
            </section>

            <!-- Embedded-signing spotlight - relocated here from the upper
                 spotlight stack so it sits with the developer / connect-your-
                 stack content: "sign inside your own app" leads naturally into
                 the integrations strip below. -->
            <marketing-spotlight
                slug="spot-iframe-embed"
                alt="Embedded signing iframe runs the ceremony inside your own application while keeping your brand front and center"
                eyebrow-icon="open_in_new"
                icon-circle-class="mkt-icon-circle--blue"
                title="Sign inside your own app"
                blurb="Drop the embedded signing iframe into your product. Your users stay on your domain, your brand stays front and center, and the signing ceremony runs inline with full audit-grade evidence behind it."
                :bullets="['Drop-in iframe - no popup, no redirect', 'Customer-domain rendering keeps your brand', 'Full ceremony lifecycle: view, sign, pay, complete', 'PostMessage events for in-app state sync']"
                cta-label="Embed signing"
                cta-href="/Home/Developers"
                flip />

            <!-- ============================================================
                 INTEGRATIONS STRIP (Light)
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">
                            Works with the tools you already use
                        </h2>
                        <p class="text-grey-7" style="font-size:1.05rem;max-width:620px;margin:0 auto;">
                            OAuth-authorized connectors on Zapier, Make.com, and HubSpot. A first-class MCP server for AI agents. Full REST API and signed webhooks for everything else.
                        </p>
                    </div>
                    <div class="row q-col-gutter-md justify-center">
                        <div class="col-6 col-sm-4 col-md-2" v-for="integ in integrations" :key="integ.key">
                            <a :href="integ.href" class="block text-decoration-none">
                                <div class="mkt-integration-tile column items-center text-center q-pa-md"
                                     style="background:#fff;border:1px solid #E2E8F0;border-radius:12px;height:100%;transition:transform 0.2s, box-shadow 0.2s, border-color 0.2s;">
                                    <div class="row items-center justify-center q-mb-md"
                                         style="width:48px;height:48px;border-radius:12px;"
                                         :style="`background:${integ.bg};`">
                                        <q-icon v-if="integ.icon" :name="integ.icon" size="28px" color="white" />
                                        <img v-else :src="integ.iconUri" :alt="integ.name + ' logo'"
                                             style="width:28px;height:28px;object-fit:contain;" loading="lazy" />
                                    </div>
                                    <div class="text-weight-bold q-mb-sm" style="font-size:0.95rem;color:#0F172A;line-height:1.2;">{{ integ.name }}</div>
                                    <span class="mkt-status-pill"
                                          :style="`background:${integ.pillBg};color:${integ.pillText};`">
                                        {{ integ.statusLabel }}
                                    </span>
                                </div>
                            </a>
                        </div>
                    </div>
                    <div class="text-center q-mt-xl">
                        <a href="/Docs/IntegrationGuides" class="text-primary text-weight-medium" style="font-size:0.95rem;">
                            Browse all integration guides <q-icon name="arrow_forward" size="16px" />
                        </a>
                    </div>
                </div>
            </section>


            <section class="mkt-light" style="padding:60px 0;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="row justify-center">
                        <div class="col-12 col-md-9 col-lg-8 text-center">
                            <p class="q-mb-sm" style="font-size:0.8rem;text-transform:uppercase;letter-spacing:0.08em;color:#6366F1;font-weight:700;">From the Launchpad family</p>
                            <h2 class="q-mb-md" style="font-size:clamp(1.5rem,2.6vw,2rem);font-weight:700;color:#0F172A;line-height:1.2;">Tools For Notaries</h2>
                            <p class="q-mb-lg" style="font-size:1.05rem;line-height:1.6;color:#475569;">
                                eSign Launchpad covers general electronic signatures. For notarized
                                documents that need a commissioned notary public on a live video
                                ceremony, our sister product
                                <a href="https://www.notarylaunchpad.com" rel="noopener" style="color:#6366F1;font-weight:600;">Notary Launchpad</a>
                                is a MISMO-certified RON platform with KBA, government-ID
                                verification, and audit-grade session recording - built for
                                title companies, lenders, and law firms.
                            </p>
                            <a href="https://www.notarylaunchpad.com" rel="noopener" class="text-no-decoration" style="display:inline-block;padding:10px 28px;border:1px solid #6366F1;border-radius:10px;color:#6366F1;font-weight:600;font-size:0.95rem;">
                                Visit Notary Launchpad
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FINAL CTA (Navy-indigo gradient)
                 ============================================================ -->
            <section class="mkt-cta-gradient reveal-scale" v-intersection="revealCfg" style="padding:80px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:clamp(1.6rem,3vw,2.4rem);">
                        Ready to simplify your signatures?
                    </h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1.1rem;max-width:500px;margin:0 auto;">
                        Join thousands of businesses that trust eSign Launchpad. Start free - no credit card required.
                    </p>
                    <div class="row justify-center q-gutter-md">
                        <q-btn color="white" text-color="primary" size="lg" label="Get Started Free" href="/Account/Register"
                            no-caps unelevated style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                        <q-btn outline color="white" size="lg" label="Contact Sales" no-caps
                            style="border-radius:10px;padding:10px 36px;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FOOTER (Dark)
                 ============================================================ -->
            <footer class="mkt-light" style="padding:60px 0 30px;border-top:1px solid #E2E8F0;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-4">
                            <div class="row items-center q-mb-md">
                                <img src="/img/logo-nav.webp" alt="eSign Launchpad" width="105" height="28" style="height: 28px; width: auto;" />
                            </div>
                            <p style="font-size:0.9rem;line-height:1.6;max-width:280px;color:#64748B;">
                                Secure, legally binding electronic signatures for businesses of all sizes. Part of the Launchpad family.
                            </p>
                        </div>
                        <div class="col-6 col-md-2">
                            <h2 class="text-weight-bold q-mb-md" style="font-size:0.85rem;text-transform:uppercase;letter-spacing:0.5px;color:#0F172A;margin-top:0;">Product</h2>
                            <div class="column q-gutter-sm">
                                <a href="/Home/Features" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Features</a>
                                <a href="/Home/Pricing" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Pricing</a>
                                <a href="/Home/Developers" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">API</a>
                                <a href="/Home/SmallBusiness" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Small Business</a>
                            </div>
                        </div>
                        <div class="col-6 col-md-2">
                            <h2 class="text-weight-bold q-mb-md" style="font-size:0.85rem;text-transform:uppercase;letter-spacing:0.5px;color:#0F172A;margin-top:0;">Company</h2>
                            <div class="column q-gutter-sm">
                                <a href="/Home/Security" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Security</a>
                                <a href="/news" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">News</a>
                                <a href="/contact" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Contact</a>
                                <a href="/privacy" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Privacy</a>
                                <a href="/terms" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Terms of Service</a>
                            </div>
                        </div>
                        <div class="col-6 col-md-2">
                            <h2 class="text-weight-bold q-mb-md" style="font-size:0.85rem;text-transform:uppercase;letter-spacing:0.5px;color:#0F172A;margin-top:0;">Resources</h2>
                            <div class="column q-gutter-sm">
                                <a href="/Docs/ApiReference" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">API Docs</a>
                                <a href="/Docs/IntegrationGuides" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Integration Guides</a>
                            </div>
                        </div>
                        <div class="col-6 col-md-2">
                            <h2 class="text-weight-bold q-mb-md" style="font-size:0.85rem;text-transform:uppercase;letter-spacing:0.5px;color:#0F172A;margin-top:0;">Family</h2>
                            <div class="column q-gutter-sm">
                                <!-- Brand-name anchor to our MISMO-certified RON sibling.
                                     Cross-linking the Launchpad family signals the brand
                                     relationship to crawlers and lets us share authority
                                     across both domains. -->
                                <a href="https://www.notarylaunchpad.com" rel="noopener" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">Notary Launchpad</a>
                                <a href="/" class="text-no-decoration" style="font-size:0.9rem;color:#475569;">eSign Launchpad</a>
                            </div>
                        </div>
                    </div>
                    <q-separator class="q-my-lg" />
                    <div class="row justify-between items-center">
                        <div style="font-size:0.8rem;color:#64748B;">
                            &copy; {{ currentYear }} eSign Launchpad. All rights reserved.
                        </div>
                        <div class="row q-gutter-sm">
                            <q-btn flat round dense icon="fab fa-twitter" color="grey-7" size="sm" aria-label="Follow eSign Launchpad on X / Twitter" />
                            <q-btn flat round dense icon="fab fa-linkedin" color="grey-7" size="sm" aria-label="Follow eSign Launchpad on LinkedIn" />
                        </div>
                    </div>
                </div>
            </footer>
        </q-page-container>
    </q-layout>
</template>

<style scoped>
/* Anchor hover styling. Inside dark sections (.mkt-dark-mid / .mkt-dark-light)
   we want links to brighten to white. Everywhere else - light sections,
   footer, cross-sell cards - keep the legible primary indigo on hover.
   The previous global `a:hover { color:#fff !important }` made footer
   links disappear against the white footer background. */
.mkt-dark-mid a:hover,
.mkt-dark-light a:hover,
.mkt-cta-gradient a:hover {
    color: #FFFFFF;
}

.mkt-card--dark {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
}

/* Integration tiles on the homepage strip - lift on hover to invite click.
   The anchor wrapping each tile uses block + no-underline so the whole tile
   becomes a click target without inheriting link styling on the inner text. */
.mkt-integration-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    border-color: #C7D2FE !important;
}

/* Status pills for integration tiles. Uppercase + letter-spacing for the
   "badge" aesthetic; tonal background (amber-50 / emerald-50) with darker
   text (amber-800 / emerald-800) gets WCAG AA 4.5:1+ at small sizes while
   reading as a coloured badge rather than plain text. */
.mkt-status-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    line-height: 1.3;
}

/* Feature-tab screenshot frame - soft tinted backdrop with the actual
   product screenshot floating inside. Aspect ratios vary per tab so we
   constrain by max-height instead of forcing a single ratio (which would
   crop or letterbox). The width/height attributes on the <img> still
   reserve layout space and prevent CLS during lazy load. */
.mkt-tab-image-frame {
    background: linear-gradient(135deg, #F5F3FF 0%, #EFF6FF 100%);
    border: 1px solid #E2E8F0;
    border-radius: 14px;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 280px;
}

.mkt-tab-image-frame img {
    display: block;
    max-width: 100%;
    width: auto;
    height: auto;
    max-height: 380px;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.10);
}

/* Speed-band: single-line summary between stats strip + persona selector.
   Subtle gradient wash so it reads as a distinct "band" without competing
   with the surrounding mkt-white sections for attention. */
.mkt-speed-band {
    background: linear-gradient(135deg, #f7f9ff 0%, #eef2ff 50%, #f3f0ff 100%);
    border-top: 1px solid #E2E8F0;
    border-bottom: 1px solid #E2E8F0;
}

.mkt-speed-eyebrow {
    display: inline-block;
    font-size: 0.78rem;
    font-weight: 700;
    color: #4F46E5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 12px;
}

.mkt-speed-headline {
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: #0F172A;
    margin: 0 0 12px 0;
}

.mkt-speed-accent {
    background: linear-gradient(135deg, #4F46E5, #7C3AED);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    white-space: nowrap;
}

.mkt-speed-subhead {
    font-size: clamp(1rem, 1.4vw, 1.15rem);
    color: #475569;
    margin: 0;
    font-weight: 500;
}
</style>

<script>
import { logout } from '@services/authActions.js';
import { getPlanByTierCode, formatPriceDisplay } from '@components/marketing/planData.js';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingSpotlight from '@components/marketing/MarketingSpotlight.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'HomeLanding',
    mixins: [revealMixin],
    components: { MarketingNav, MarketingSpotlight },
    data() {
        return {
            isAuthenticated: typeof _AUTHENTICATED !== 'undefined' && _AUTHENTICATED === true,
            mobileNav: false,
            activeTab: 'send',
            currentYear: new Date().getFullYear(),

            // Hero feature-highlight rotator. Each card spotlights one
            // capability and the price/availability that backs it up. Copy
            // stays capability-descriptive - no comparisons, no uniqueness
            // claims. heroIdx is the active card; heroTimer holds the rotation.
            heroIdx: 0,
            heroTimer: null,
            heroFeatures: [
                {
                    key: 'api',
                    eyebrow: 'Self-serve developer API',
                    stat: '22+',
                    statLabel: 'API endpoints',
                    title: 'Full developer API + 24 events',
                    body: 'Embedded iframe signing. Cryptographically-signed event notifications. SDKs and integration guides - everything you need to build signing into your product.',
                    anchor: 'From $49/mo · pay only for what you use',
                    caption: 'Build signing into your product · API + events',
                    color: '#4F46E5',
                    colorTo: '#7C3AED'
                },
                {
                    key: 'sso',
                    eyebrow: 'Self-serve enterprise identity',
                    stat: '$149',
                    statLabel: '/mo · SSO + auto-provisioning',
                    title: 'Single sign-on for the whole org',
                    body: 'Single sign-on with Okta, Microsoft Entra ID, Google Workspace, JumpCloud. Automatic user provisioning. Auto-create on first login.',
                    anchor: 'Live in 10 minutes · no sales call needed',
                    caption: 'Enterprise identity, SaaS-priced',
                    color: '#0D9488',
                    colorTo: '#047857'
                },
                {
                    key: 'audit',
                    eyebrow: 'Included with every plan',
                    stat: 'SHA-256',
                    statLabel: 'hash-chained audit',
                    title: 'Tamper-evident audit trail',
                    body: '30+ event types. IP and timestamp on every action. Each block embeds the prior block\'s hash - any change is mathematically detectable.',
                    anchor: 'On every plan · including Free',
                    caption: 'Audit-grade compliance, included',
                    color: '#0EA5E9',
                    colorTo: '#4F46E5'
                },
                {
                    key: 'mcp',
                    eyebrow: 'Built for AI agents',
                    stat: '90+',
                    statLabel: 'MCP tools for Claude',
                    title: 'AI agents drive your signings',
                    body: 'Drop our MCP server into Claude Desktop or any MCP client. Send packages, add signers, void documents - all from natural language.',
                    anchor: 'Drive signings from your AI assistant',
                    caption: 'Claude-native via Model Context Protocol',
                    color: '#B45309',
                    colorTo: '#EF4444'
                }
            ],

            // Audience selector - collapsed to 3 cards mapping 1:1 to hub pages.
            // Each hub page expands on the audience's use cases, features,
            // and pricing (deeper than a single plan card could).
            personaCards: [
                {
                    key: 'individual',
                    emoji: '\uD83D\uDCC4',
                    label: 'For Individuals',
                    subtitle: '3 free signings/mo, forever. $4.99 per additional signing. No credit card.',
                    tierName: 'Free tier',
                    tierPrice: '3 free / mo',
                    tierColor: '#64748B',
                    href: '/Home/Individuals'
                },
                {
                    key: 'smb',
                    emoji: '\uD83C\uDFE2',
                    label: 'Small Business & Enterprise',
                    subtitle: '3-100+ person teams. Payments, workflow, single sign-on.',
                    tierName: '4 plans',
                    tierPrice: '$19 - $149 / mo',
                    tierColor: '#4F46E5',
                    href: '/Home/SmallBusiness'
                },
                {
                    key: 'developer',
                    emoji: '\uD83E\uDDE9',
                    label: 'Developers & AI Agents',
                    subtitle: 'REST API, embedded signing, MCP-native for Claude.',
                    tierName: 'Developer tier',
                    tierPrice: '$49 / mo',
                    tierColor: '#7C3AED',
                    href: '/Home/Developers'
                }
            ],

            // Three declarative commitment cards. Each card eyebrow matches
            // one word from the section headline (Predictable / Focused /
            // Fast). Card titles state the commitment outright. Descriptions
            // back it with concrete numbers and product decisions. Anchor
            // bands are short, hard-stop statements - no hedging, no
            // self-deprecation.
            painPoints: [
                {
                    icon: 'price_check',
                    color: '#047857',
                    eyebrow: 'Predictable',
                    title: 'Pricing you can forecast',
                    description: 'Unlimited documents on every paid plan. Per-signing overage from $0.25. No per-envelope fees, no surprise tier upgrades.',
                    solution: 'Unlimited documents. Every paid plan.'
                },
                {
                    icon: 'center_focus_strong',
                    color: '#4F46E5',
                    eyebrow: 'Focused',
                    title: 'Send, sign, audit. Nothing else.',
                    description: 'We built the three capabilities that matter, and stopped. No CRM bolt-ons. No vendor management. No surface area your team will never touch.',
                    solution: 'A focused product. Zero bloat.'
                },
                {
                    icon: 'rocket_launch',
                    color: '#B45309',
                    eyebrow: 'Fast',
                    title: 'Signup to first send in 15 minutes',
                    description: 'Self-serve onboarding, drag-and-drop document prep, smart field detection. No implementation call required. The dashboard is the manual.',
                    solution: 'Live in 15 minutes.'
                }
            ],

            // Big-number stats strip in the hero band.
            stats: [
                { num: 22,  suffix: '+', label: 'API endpoints',   from: '#4F46E5', to: '#7C3AED' },
                { num: 24,               label: 'Webhook events',  from: '#7C3AED', to: '#EC4899' },
                { num: 18,               label: 'Field types',     from: '#0EA5E9', to: '#4F46E5' },
                { num: 50,               label: 'States covered',  from: '#047857', to: '#0D9488' },
                { num: 256,              label: 'Bit Encryption', from: '#B45309', to: '#EF4444' },
                { num: 0,   prefix: '$', label: 'To start',        from: '#047857', to: '#4F46E5' }
            ],
            statCounters: [0, 0, 0, 0, 0, 0],

            // Real infrastructure partners - single source of truth for the
            // grayscale logo strip + Security page partner cards.
            partners: [
                { name: 'Microsoft Azure',   role: 'Cloud',          icon: 'cloud' },
                { name: 'Stripe',            role: 'Payments',       icon: 'payments' },
                { name: 'Persona',           role: 'ID verification', icon: 'badge' },
                { name: 'Authenticate.com',  role: 'KBA quizzes',    icon: 'quiz' }
            ],

            featureTabs: [
                {
                    key: 'send', label: 'Send & Sign', icon: 'send',
                    heading: 'Upload, prepare, and send in minutes',
                    description: 'Drag-and-drop docs. Smart field detection. Sequential or simultaneous signing.',
                    visual: 'upload_file',
                    image: {
                        alt: 'Upload PDF or Word documents and send for electronic signature in eSign Launchpad',
                        widths: [480, 800, 1200],
                        intrinsicW: 1200, intrinsicH: 962
                    },
                    features: [
                        'PDF & Word with auto-conversion',
                        '18 field types',
                        'Smart text-tag field detection',
                        'Sequential or simultaneous signing',
                        'Custom email per recipient'
                    ]
                },
                {
                    key: 'templates', label: 'Templates', icon: 'content_copy',
                    heading: 'Reusable templates, one-click sends',
                    description: 'Pre-place fields once. Reuse forever. Bulk-send to many recipients from one template.',
                    visual: 'file_copy',
                    image: {
                        alt: 'Reusable signing templates with role-based fields and bulk-send in eSign Launchpad',
                        widths: [480, 800, 1200],
                        intrinsicW: 1200, intrinsicH: 1242
                    },
                    features: [
                        'Up to 1,000 templates on Business',
                        'Role-based (e.g. "Buyer", "Seller")',
                        'Pre-place fields, reuse forever',
                        'Clone + customize for variations',
                        'Bulk send from one template'
                    ]
                },
                {
                    key: 'team', label: 'Teams', icon: 'groups',
                    heading: 'Collaborate with your team',
                    description: 'Signing groups, approval workflows, and 6 roles for any chain of command.',
                    visual: 'group_work',
                    image: {
                        alt: 'Team roles, signing groups, and approval workflows for collaborative document signing',
                        widths: [480, 800, 1200],
                        intrinsicW: 1200, intrinsicH: 685
                    },
                    features: [
                        'Signing groups (any member signs)',
                        '6 roles incl. Approver + Editor',
                        'Approval workflows before signing',
                        'Role-based access control',
                        'Unlimited users on Business'
                    ]
                },
                {
                    key: 'security', label: 'Security', icon: 'shield',
                    heading: 'Bank-level security, always on',
                    description: '256-bit encryption at rest and in transit. Tamper-evident audit trail. ID verification on demand.',
                    visual: 'security',
                    image: {
                        alt: 'Layered security architecture covering identity, threat protection, compliance, and audit for electronic signatures',
                        widths: [480, 800, 1200],
                        intrinsicW: 1200, intrinsicH: 675
                    },
                    features: [
                        '256-bit encryption everywhere',
                        'Enterprise cloud infrastructure',
                        'SMS · knowledge-based · ID verification',
                        'Hash-chained audit trail',
                        'Certificate + evidence package'
                    ]
                },
                {
                    key: 'api', label: 'API', icon: 'code',
                    heading: 'Build signing into your product',
                    description: 'Developer API, real-time event notifications, and AI-agent integration.',
                    visual: 'integration_instructions',
                    image: {
                        alt: 'REST API for embedding electronic signatures and the MCP server for AI agents',
                        widths: [480, 800, 1200],
                        intrinsicW: 1200, intrinsicH: 867
                    },
                    features: [
                        '22+ API endpoints',
                        '24 cryptographically-signed events',
                        'Embedded signing for white-label',
                        'AI-agent integration via MCP',
                        'Complete docs + guides'
                    ]
                }
            ],

            steps: [
                {
                    number: '1',
                    icon: 'cloud_upload',
                    title: 'Upload your document',
                    description: 'Drag in any PDF or Word doc. Smart detection finds fields automatically.'
                },
                {
                    number: '2',
                    icon: 'person_add',
                    title: 'Add recipients',
                    description: 'Set signing order. Pick auth method. Customize the email.'
                },
                {
                    number: '3',
                    icon: 'task_alt',
                    title: 'Get it signed',
                    description: 'Recipients sign from any device. Everyone gets a copy + full audit trail.'
                }
            ],

            // tierTeaser is now a computed property - see below. It reads from
            // getPlans() in planData.js so the home-page tile prices always
            // match Sign_SubscriptionPlans without a separate hardcoded array.

            trustBadges: [
                { icon: 'gavel', label: 'ESIGN Act', sub: '& UETA' },
                { icon: 'lock', label: '256-bit', sub: 'Encryption' },
                { icon: 'verified', label: 'Audit Trail', sub: 'Tamper-evident' },
                { icon: 'fingerprint', label: 'ID Verification', sub: 'Built-in' },
                { icon: 'cloud', label: 'Enterprise', sub: 'Cloud Infrastructure' },
                { icon: 'history', label: '7-Year', sub: 'Retention' }
            ],
            integrations: [
                {
                    key: 'zapier',
                    name: 'Zapier',
                    bg: '#E04300',
                    icon: 'bolt',
                    statusLabel: 'Invite Only',
                    pillBg: '#FEF3C7',
                    pillText: '#92400E',
                    href: '/Docs/IntegrationGuides?topic=zapier'
                },
                {
                    key: 'make',
                    name: 'Make.com',
                    bg: '#6D00CC',
                    icon: 'account_tree',
                    statusLabel: 'Invite Only',
                    pillBg: '#FEF3C7',
                    pillText: '#92400E',
                    href: '/Docs/IntegrationGuides?topic=make'
                },
                {
                    key: 'hubspot',
                    name: 'HubSpot',
                    bg: '#D14628',
                    icon: 'hub',
                    statusLabel: 'Beta',
                    pillBg: '#FEF3C7',
                    pillText: '#92400E',
                    href: '/Docs/IntegrationGuides?topic=hubspot'
                },
                {
                    key: 'mcp',
                    name: 'AI Agents',
                    bg: '#4F46E5',
                    icon: 'smart_toy',
                    statusLabel: 'Available',
                    pillBg: '#D1FAE5',
                    pillText: '#065F46',
                    href: '/Docs/IntegrationGuides?topic=ai-agents'
                },
                {
                    key: 'webhooks',
                    name: 'Webhooks',
                    bg: '#0284C7',
                    icon: 'webhook',
                    statusLabel: 'Available',
                    pillBg: '#D1FAE5',
                    pillText: '#065F46',
                    href: '/Docs/IntegrationGuides?topic=webhooks'
                },
                {
                    key: 'rest-api',
                    name: 'REST API',
                    bg: '#047857',
                    icon: 'api',
                    statusLabel: 'Available',
                    pillBg: '#D1FAE5',
                    pillText: '#065F46',
                    href: '/Docs/IntegrationGuides?topic=rest-api'
                }
            ]
        };
    },
    computed: {
        // Stats-strip intersection config - same rootMargin as the shared
        // revealCfg, but routes through our local handler so we can trigger
        // the count-up animation alongside the reveal class toggle.
        statsRevealCfg() {
            return {
                handler: this.onStatsReveal,
                cfg: { rootMargin: '0px 0px -15% 0px' }
            };
        },
        // Active feature-showcase tab. Resolves activeTab key to the full
        // tab object so the template renders only one panel - letting the
        // <transition> animate cleanly between swaps. Falls back to the
        // first tab if activeTab somehow points at a missing key.
        currentTab() {
            return this.featureTabs.find(t => t.key === this.activeTab) || this.featureTabs[0];
        },

        // Homepage tier teaser - renders the four-tile "Plans starting at $0/month"
        // row. Pulls live data from _pageConfig.plans (with bundled fallback).
        tierTeaser() {
            const build = (tierCode, { featured = false, subOverride = null } = {}) => {
                const plan = getPlanByTierCode(tierCode);
                if (!plan) return null;
                const price = formatPriceDisplay(plan, { annual: false });
                let sub;
                if (subOverride) {
                    sub = subOverride;
                } else if (plan.tierCode === 'free') {
                    sub = `${plan.packageLimit} free/mo · $${plan.overagePerPackage.toFixed(2)} each after`;
                } else {
                    const parts = [];
                    parts.push('/mo');
                    if (plan.packageLimit > 0) parts.push(`${plan.packageLimit} signings`);
                    if (plan.baseUserCount === 1) parts.push('1 user');
                    else parts.push(`${plan.baseUserCount} users`);
                    sub = parts.join(' · ');
                }
                return { tierCode: plan.tierCode, name: plan.name, price, sub, featured };
            };
            return [
                build('free'),
                build('starter'),
                build('pro', { featured: true, subOverride: '/mo · 500 signings · API included' }),
                build('business', { subOverride: '/mo · SSO + auto-provisioning · 10 users' })
            ].filter(Boolean);
        }
    },
    methods: {
        logout,
        reloadPage() {
            if (typeof window !== 'undefined') {
                window.location.reload();
            }
        },

        // Build a `srcset` string for a feature-tab image. Format is
        // `/img/features/{key}-{w}.{ext} {w}w, ...` - used by both the
        // WebP <source> and the JPG fallback <img>.
        srcsetFor(tab, ext) {
            return tab.image.widths
                .map(w => `/img/features/${tab.key}-${w}.${ext} ${w}w`)
                .join(', ');
        },

        // Stats strip - adds the reveal class AND kicks off the count-up
        // animation when the section enters the viewport. Returning false
        // tells Quasar's v-intersection to stop observing.
        onStatsReveal(entry) {
            if (entry.isIntersecting) {
                entry.target.classList.add('is-revealed');
                this.animateStats();
                return false;
            }
        },

        // Slot-machine count-up: ~500ms of rapid random scrambling (each
        // counter cycles through random integers up to 2× its target every
        // animation frame), then ~1300ms of eased count-up to the final
        // target. Total ~1.8s. _statsAnimated guard prevents re-running on
        // any subsequent intersection event.
        animateStats() {
            if (this._statsAnimated) return;
            this._statsAnimated = true;
            const targets = this.stats.map(s => s.num);
            const scrambleMs = 500;
            const totalMs = 1800;
            const start = performance.now();
            const tick = (now) => {
                const elapsed = now - start;
                if (elapsed < scrambleMs) {
                    this.statCounters = targets.map(t => {
                        const ceiling = Math.max(t * 2, 8);
                        return Math.floor(Math.random() * ceiling);
                    });
                    requestAnimationFrame(tick);
                } else if (elapsed < totalMs) {
                    const p = (elapsed - scrambleMs) / (totalMs - scrambleMs);
                    const eased = 1 - Math.pow(1 - p, 3);
                    this.statCounters = targets.map(t => Math.round(t * eased));
                    requestAnimationFrame(tick);
                } else {
                    this.statCounters = [...targets];
                }
            };
            requestAnimationFrame(tick);
        },

        // Hero carousel - rotation lives entirely in the timer, not in a
        // computed/watch, so dot-clicks can short-circuit and reset the timer
        // cleanly. Interval is 4s as agreed; cleared in beforeUnmount.
        startHeroCarousel() {
            if (this.heroTimer) clearInterval(this.heroTimer);
            this.heroTimer = setInterval(() => {
                this.heroIdx = (this.heroIdx + 1) % this.heroFeatures.length;
            }, 4000);
        },
        pauseHeroCarousel() {
            if (this.heroTimer) {
                clearInterval(this.heroTimer);
                this.heroTimer = null;
            }
        },
        resumeHeroCarousel() {
            // Don't double-start if mouseleave fires after hidden tab restore.
            if (!this.heroTimer) this.startHeroCarousel();
        },
        setHeroIdx(i) {
            this.heroIdx = i;
            // Reset the timer so the user gets a full 4s on their pick before
            // the carousel moves on.
            this.startHeroCarousel();
        }
    },
    mounted() {
        this.startHeroCarousel();
    },
    beforeUnmount() {
        if (this.heroTimer) clearInterval(this.heroTimer);
    }
};
</script>
